import {
  ApprovalItemLiteModel,
  ApprovalVerdictStatusEnum,
  useGetApprovalItems,
} from 'api-hooks/approval-item';
import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { AuthorizationRules } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import {
  Container,
  NotificationBadge,
  TopContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ApprovalItemTable from './components/table';

export default function ApprovalItemList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<ApprovalItemLiteModel>[]>(
    () => [
      {
        header: t('common:number_text'),
        accessorKey: 'number',
        minSize: 140,
        size: 140,
      },
      {
        header: t('common:type'),
        accessorKey: 'type',
        minSize: 140,
        size: 140,
      },
      {
        header: t('common:request_from'),
        accessorKey: 'employee.name',
        minSize: 140,
        size: 140,
      },
      {
        header: t('common:approval_level'),
        accessorKey: 'currentOrder',
        minSize: 140,
        size: 140,
      },
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        sortName: 'created_at',
        minSize: 200,
        cell: ({ getValue }) => <>{formatDate(getValue() as Date)}</>,
      },
      {
        header: t('common:updated_at'),
        accessorKey: 'updatedAt',
        sortName: 'updated_at',
        minSize: 200,
        cell: ({ getValue }) => <>{formatDate(getValue() as Date)}</>,
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetApprovalItems,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.ApprovalItemsPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message || '' });
    }
  }, [error]);

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          title={t('common:approval')}
          showAdd={false}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          query={query}
        />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>

        <Tabs
          activeKey={activeKey}
          onChangeActive={({ activeKey }) => {
            setActiveKey(activeKey);
            refetchNotification();
            setFilters();
            if (activeKey === '0') {
              setStatus(`${ApprovalVerdictStatusEnum.Pending}`);
            }
            if (activeKey === '1') {
              setStatus(
                `${ApprovalVerdictStatusEnum.Approved}|${ApprovalVerdictStatusEnum.Rejected}`,
              );
            }
          }}
          items={[
            {
              title: (
                <>
                  {t('common:pending')}
                  {!!dataNotification?.data?.[
                    ApprovalVerdictStatusEnum.Pending
                  ] &&
                    !!dataNotification?.data?.[
                      ApprovalVerdictStatusEnum.Pending
                    ].count && (
                      <>
                        <Separator gap={8} />
                        <NotificationBadge
                          style={{
                            backgroundColor:
                              dataNotification?.data?.[
                                ApprovalVerdictStatusEnum.Pending
                              ].background_color,
                            color:
                              dataNotification?.data?.[
                                ApprovalVerdictStatusEnum.Pending
                              ].color,
                          }}
                        >
                          {
                            dataNotification?.data?.[
                              ApprovalVerdictStatusEnum.Pending
                            ].count
                          }
                        </NotificationBadge>
                      </>
                    )}
                </>
              ),
              component: <></>,
            },
            { title: t('common:complete'), component: <></> },
          ]}
          overrideStyles={{
            tabList: { minHeight: '0px' },
            tab: { minHeight: '0px', height: '0', flexGrow: '0' },
            tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
          }}
        />

        <ApprovalItemTable
          approval={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          sortColumns={columns || []}
          meta={data?.meta}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
